/* eslint-disable max-lines */
/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IBulkNotifyTripEntry, IBulkUpdatedTripAffected } from "../../../interfaces/endpoints/trips/IBulkEditTripsWorkingDays";
import { IJourneyReservationPassengerInfo } from "../../../interfaces/endpoints/trips/TripChangeNotification";

interface BulkAffectedReservationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: IBulkNotifyTripEntry[]) => void;
  affectedByTrip: IBulkUpdatedTripAffected[];
  tripLabelById?: Record<number, string>;
}

const parseYmdToUtcDate = (ymd: string) => {
  const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return new Date(ymd);
  return new Date(Date.UTC(y, m - 1, d));
};

const groupByDate = (passengers: IJourneyReservationPassengerInfo[]) =>
  passengers.reduce((acc, reservation) => {
    const date = reservation.journeyDate || "Datë e panjohur";
    if (!acc[date]) acc[date] = [] as IJourneyReservationPassengerInfo[];
    acc[date].push(reservation);
    return acc;
  }, {} as Record<string, IJourneyReservationPassengerInfo[]>);

const BulkAffectedReservationsModal: React.FC<BulkAffectedReservationsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  affectedByTrip,
  tripLabelById
}) => {
  // tripId -> Set of selected reservation IDs
  const [selectedByTrip, setSelectedByTrip] = useState<Map<number, Set<string>>>(new Map());
  const [expandedTrips, setExpandedTrips] = useState<Set<number>>(new Set());
  const didInit = useRef(false);

  const longDateFormatter = useMemo(
    () => new Intl.DateTimeFormat("sq", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC"
    }),
    []
  );

  const shortDateFormatter = useMemo(
    () => new Intl.DateTimeFormat("sq", {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC"
    }),
    []
  );

  const tripsWithPassengers = useMemo(
    () => affectedByTrip.filter((t) => t.affected?.affectedPassengers?.length > 0),
    [affectedByTrip]
  );

  const totalReservations = useMemo(
    () => tripsWithPassengers.reduce((sum, t) => sum + t.affected.affectedPassengers.length, 0),
    [tripsWithPassengers]
  );

  const totalSelected = useMemo(() => {
    let s = 0;
    selectedByTrip.forEach((set) => {
      s += set.size;
    });
    return s;
  }, [selectedByTrip]);

  useEffect(() => {
    if (isOpen && !didInit.current && tripsWithPassengers.length > 0) {
      const initial = new Map<number, Set<string>>();
      const initialExpanded = new Set<number>();
      tripsWithPassengers.forEach((t) => {
        initial.set(t.tripId, new Set(t.affected.affectedPassengers.map((p) => p.reservationId)));
        initialExpanded.add(t.tripId);
      });
      setSelectedByTrip(initial);
      setExpandedTrips(initialExpanded);
      didInit.current = true;
    }
  }, [isOpen, tripsWithPassengers]);

  useEffect(() => {
    if (!isOpen) {
      didInit.current = false;
      setSelectedByTrip(new Map());
      setExpandedTrips(new Set());
    }
  }, [isOpen]);

  const setTripSelection = (tripId: number, next: Set<string>) => {
    setSelectedByTrip((prev) => {
      const m = new Map(prev);
      m.set(tripId, next);
      return m;
    });
  };

  const handleTripToggleAll = (tripId: number, allIds: string[], checked: boolean) => {
    setTripSelection(tripId, checked ? new Set(allIds) : new Set());
  };

  const handleDateToggle = (tripId: number, dateReservations: IJourneyReservationPassengerInfo[], checked: boolean) => {
    const current = new Set(selectedByTrip.get(tripId) ?? []);
    dateReservations.forEach((r) => {
      if (checked) current.add(r.reservationId);
      else current.delete(r.reservationId);
    });
    setTripSelection(tripId, current);
  };

  const handleReservationToggle = (tripId: number, reservationId: string, checked: boolean) => {
    const current = new Set(selectedByTrip.get(tripId) ?? []);
    if (checked) current.add(reservationId);
    else current.delete(reservationId);
    setTripSelection(tripId, current);
  };

  const handleSelectAll = () => {
    const next = new Map<number, Set<string>>();
    tripsWithPassengers.forEach((t) => {
      next.set(t.tripId, new Set(t.affected.affectedPassengers.map((p) => p.reservationId)));
    });
    setSelectedByTrip(next);
  };

  const handleDeselectAll = () => {
    const next = new Map<number, Set<string>>();
    tripsWithPassengers.forEach((t) => {
      next.set(t.tripId, new Set());
    });
    setSelectedByTrip(next);
  };

  const handleTripExpandToggle = (tripId: number) => {
    setExpandedTrips((prev) => {
      const n = new Set(prev);
      if (n.has(tripId)) n.delete(tripId);
      else n.add(tripId);
      return n;
    });
  };

  const handleConfirm = () => {
    const selection: IBulkNotifyTripEntry[] = tripsWithPassengers.map((t) => ({
      tripId: t.tripId,
      reservationIds: Array.from(selectedByTrip.get(t.tripId) ?? new Set<string>())
    }));
    onConfirm(selection);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start
    justify-center z-50 p-2 sm:p-4 pt-16">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b
        border-gray-200 bg-white rounded-t-lg">
          <h2 className="text-lg sm:text-xl font-semibold">
            Njofto pasagjerët e prekur ({totalReservations} rezervime, {tripsWithPassengers.length} orare)
          </h2>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md
            hover:bg-gray-50 transition-colors flex items-center gap-2"
            aria-label="Vazhdo pa njoftuar"
          >
            <span>Vazhdo pa njoftuar</span>
            <span aria-hidden>×</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-4 text-sm text-gray-600">
            <p>
              Oraret janë përditësuar. Zgjidhni cilat rezervime duhet të marrin njoftime me email
              për secilin orar të prekur:
            </p>
            {totalReservations > 0 && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-blue-800 font-medium">Përmbledhje:</p>
                <ul className="text-blue-700 text-xs mt-1 space-y-1">
                  <li>• Total orare të prekura: {tripsWithPassengers.length}</li>
                  <li>• Total rezervime të prekura: {totalReservations}</li>
                  <li>• Të gjitha rezervimet janë zgjedhur si parazgjedhje</li>
                  <li>• Klikoni mbi titullin e orarit për ta zgjeruar/mbyllur</li>
                  <li>• Përdorni kutitë e datës ose ato individuale për përzgjedhje të hollësishme</li>
                </ul>
              </div>
            )}
          </div>

          <div
            className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4
            space-y-3 sm:space-y-0"
          >
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSelectAll}
                className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200
                transition-colors"
              >
                Zgjidh të gjitha
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200
                transition-colors"
              >
                Çzgjedh të gjitha
              </button>
            </div>
            <div className="text-sm text-gray-600 text-center sm:text-right">
              {totalSelected} nga {totalReservations} rezervime të zgjedhura
            </div>
          </div>

          <div className="space-y-4">
            {tripsWithPassengers.map((tripGroup) => {
              const { tripId, affected } = tripGroup;
              const passengers = affected.affectedPassengers;
              const reservationsByDate = groupByDate(passengers);
              const allIds = passengers.map((p) => p.reservationId);
              const selectedSet = selectedByTrip.get(tripId) ?? new Set<string>();
              const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));
              const someSelected = allIds.some((id) => selectedSet.has(id));
              const isExpanded = expandedTrips.has(tripId);
              const label = tripLabelById?.[tripId] ?? affected.tripChanges?.tripName ?? `Orari ${tripId}`;
              const changeDescription = affected.tripChanges?.changeDescription
                ?? affected.tripChanges?.tripTimeChange?.changeDescription;

              return (
                <div key={tripId} className="border border-gray-300 rounded-lg overflow-hidden">
                  <div className="flex items-center bg-gray-50 px-3 py-3 sm:px-4">
                    <input
                      type="checkbox"
                      id={`trip-${tripId}`}
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && someSelected;
                      }}
                      onChange={(e) => handleTripToggleAll(tripId, allIds, e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleTripExpandToggle(tripId)}
                      className="flex-1 text-left flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-sm sm:text-base text-gray-800 break-words">
                          {label}
                        </div>
                        {changeDescription && (
                          <div className="text-xs text-gray-500 mt-0.5 break-words">
                            {changeDescription}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-gray-500">
                          {selectedSet.size}/{passengers.length}
                        </span>
                        <span aria-hidden className="text-gray-400">
                          {isExpanded ? "▲" : "▼"}
                        </span>
                      </div>
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="p-3 sm:p-4 space-y-4">
                      {Object.entries(reservationsByDate).map(([date, reservations]) => {
                        const dateAllSelected = reservations.every((r) => selectedSet.has(r.reservationId));
                        return (
                          <div key={`${tripId}-${date}`} className="border rounded-lg p-3 sm:p-4">
                            <div className="flex items-center mb-3">
                              <input
                                type="checkbox"
                                id={`trip-${tripId}-date-${date}`}
                                checked={dateAllSelected}
                                onChange={(e) => handleDateToggle(tripId, reservations, e.target.checked)}
                                className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              />
                              <label
                                htmlFor={`trip-${tripId}-date-${date}`}
                                className="font-medium text-base sm:text-lg flex-1"
                              >
                                {date === "Datë e panjohur" ? (
                                  "Datë e panjohur"
                                ) : (
                                  <span>
                                    <span className="hidden sm:inline">
                                      {longDateFormatter.format(parseYmdToUtcDate(date))}
                                    </span>
                                    <span className="sm:hidden">
                                      {shortDateFormatter.format(parseYmdToUtcDate(date))}
                                    </span>
                                  </span>
                                )}
                              </label>
                              <span className="text-sm text-gray-500 ml-2">
                                ({reservations.length} rezervim{reservations.length !== 1 ? "e" : ""})
                              </span>
                            </div>

                            <div className="ml-6 sm:ml-8 space-y-2">
                              {reservations.map((reservation) => (
                                <div key={`${tripId}-${reservation.reservationId}`} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`trip-${tripId}-res-${reservation.reservationId}`}
                                    checked={selectedSet.has(reservation.reservationId)}
                                    onChange={(e) => handleReservationToggle(tripId, reservation.reservationId, e.target.checked)}
                                    className="mr-3 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                  />
                                  <label
                                    htmlFor={`trip-${tripId}-res-${reservation.reservationId}`}
                                    className="text-sm flex-1"
                                  >
                                    <span className="font-medium break-all">{reservation.email}</span>
                                    {reservation.hasActiveConcierge && (
                                      <span className="ml-2 text-green-600 text-xs">✓ Konciergj</span>
                                    )}
                                    <span className="ml-2 text-gray-500 break-all">
                                      ({reservation.reservationId})
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50 rounded-b-lg">
          <div
            className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0"
          >
            <div className="text-sm text-gray-600" />
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark
                transition-colors flex items-center justify-center space-x-2"
              >
                <span>Njofto rezervimet e zgjedhura</span>
                <span className="bg-primary-dark px-2 py-1 rounded text-xs">
                  {totalSelected}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAffectedReservationsModal;
